import axios from "axios";
import {getCookie, isEmpty, randomChoice} from "../utils.js";
import {TimeDelta} from "../time.js";
import {formatDate} from "../utils.js"
import {Cache} from "../cache.js";

export const ABORTED = "ABORTED";


const buildBackend = (subDomain, tld, prefix) => {
    const baseURL = getBaseUrl(subDomain, tld, prefix);
    let instance = axios.create({
        baseURL: baseURL,
        headers: {
            "Accept": "application/json",
            "Content-Type": "application/json",
        },
    });

    instance.interceptors.response.use(function (response) {
        return response;
    }, function (error) {
        if (error?.code === "ERR_CANCELED") {
            console.warn("Request was aborted", error);
            return Promise.reject(error);
        }
        if (error?.code === "ERR_NETWORK") {
            console.error("Network error occurred", error);
            return Promise.reject(error);
        }
        if (error.response === undefined) {
            console.error("User is not authenticated or not authorized", error);
            return Promise.reject(error);
        }
        if (error.response.status === 401 || error.response.status === 403) {
            console.error("User is not authenticated or not authorized", error);
            return Promise.reject(error);
        } else {
            console.error("Intercepted a non-authorization related error; forwarding along.", error);
            return Promise.reject(error);
        }
    });

    instance.defaults.xsrfHeaderName = "X-CSRFToken";
    instance.defaults.xsrfCookieName = "csrftoken2";
    instance.defaults.withCredentials = true;

    return instance;
};


export const getBaseUrl = (subDomain, tld, apiPrefix) => {
    const domain = getDomain(subDomain, tld);
    return `${process.env.REACT_APP_HTTP_SCHEME}://${domain}${apiPrefix}`;
};

export const getDomain = (subDomain, tld) => {
    return subDomain === null || subDomain === "" ? tld : `${subDomain}.${tld}`;
};

export const getSubdomains = (subDomainsArg) => {
    if (isEmpty(subDomainsArg)) {
        return [null];
    }
    const subDomains = JSON.parse(subDomainsArg);
    if (subDomains.length === 0) {
        subDomains.push(null);
    }
    return subDomains;
};

class Backend {

    static inProgress = new Map();

    constructor(subDomain, tld, prefix) {
        this.id = `${subDomain}-${tld}-${prefix}` + Math.random().toString(36).substring(2, 15);
        this.instance = buildBackend(subDomain, tld, prefix);
        this.baseUrl = getBaseUrl(subDomain, tld, prefix);

        let cacheName = [];
        if (!isEmpty(subDomain)) {
            cacheName.push(subDomain);
        }
        if (!isEmpty(tld)) {
            cacheName.push(tld);
        }

        let storeName = [];
        if (!isEmpty(prefix)) {
            storeName.push(prefix);
        }
        storeName.push("cache");

        this.cache = Cache.create(cacheName.join("-"), storeName.join("-"));
    }

    _getRequestKey(url, options) {
        return this.baseUrl + url + "?" + this.normalizeParams(options.params || {});
    }

    _startSharedRequest(requestKey, url, options = {}) {
        const controller = new AbortController();
        const axiosOptions = {...options, signal: controller.signal};
        const sharedPromise = this.instance.get(url, axiosOptions);
        const entry = {
            requestKey,
            controller,
            subscribers: new Set(),
            settled: false,
            promise: sharedPromise.then((response) => {
                entry.settled = true;
                Backend.inProgress.delete(requestKey);
                return response;
            })
        };
        Backend.inProgress.set(requestKey, entry);
        return entry;
    }

    _createCanceledError() {
        const error = new Error("canceled");
        error.code = "ERR_CANCELED";
        error.name = "CanceledError";
        return error;
    }

    _subscribe(entry, subscriberSignal) {
        const token = Symbol('subscriber');
        entry.subscribers.add(token);

        return new Promise((resolve, reject) => {
            let done = false;

            const cleanup = () => {
                if (done) return;
                done = true;
                entry.subscribers.delete(token);
                // If there are no more subscribers and the request is not yet settled, abort it.
                if (!entry.settled && entry.subscribers.size === 0) {
                    Backend.inProgress.delete(entry.requestKey);
                    try {entry.controller.abort('last-subscriber-cancelled');} catch (e) {}
                }
            }

            const onAbort = () => {
                if (subscriberSignal && !subscriberSignal.aborted) {
                    console.error("onAbort called but subscriberSignal is not aborted?", subscriberSignal);
                    return;
                }
                cleanup();
                reject(this._createCanceledError())
            };

            if (subscriberSignal) {
                if (subscriberSignal.aborted) {
                    return onAbort();
                }
                subscriberSignal.addEventListener('abort', onAbort, {once: true});
            }

            return entry.promise.then((value) => {
                cleanup();
                return resolve(value);
            }, (err) => {
                cleanup();
                return reject(err);
            });
        });
    }

    getInProgressRequest(requestKey) {
        return Backend.inProgress.get(requestKey);
    }

    isCanceled = (err) => {
        return err?.code === "ERR_CANCELED"
            || err?.name === "CanceledError"
            || (err?.message && err.message.toLowerCase().includes("canceled"))
            || (err?.message && err.message.toLowerCase().includes("aborted"))
            || (err?.cause?.name === "AbortError");
    }

    get = (url, options, abortController) => {
        const requestKey = this._getRequestKey(url, options);

        if (options?.cache) {
            return this.cache.get(requestKey).then(cached => {
                if (!isEmpty(cached)) {
                    const {value, expiry} = JSON.parse(cached);
                    if (!isEmpty(value) && Date.now() < expiry) {
                        return Promise.resolve(value);
                    }
                }
                return this.getOrStartRequest(url, options, abortController, requestKey).then(response => {
                    if (response && response.data && response.status === 200) {
                        return this.cache.put(
                            requestKey,
                            JSON.stringify({
                                value: response,
                                expiry: Date.now() + (60 * 60 * 12 * 1000) // Cache for 12 hours
                            })
                        ).catch(err => {
                            console.error("Error putting in cache", err);
                        }).then(() => response);
                    }
                    return response;
                });
            }).catch(err => {
                if (this.isCanceled(err)) {
                    return Promise.reject(err);
                }
                console.error("Error getting from cache", err);
                return this.getOrStartRequest(url, options, abortController, requestKey);
            });
        }

        return this.getOrStartRequest(url, options, abortController, requestKey);
    }

    getOrStartRequest = (url, options, abortController, requestKey) => {
        let entry = this.getInProgressRequest(requestKey) || this._startSharedRequest(requestKey, url, options);
        return this._subscribe(entry, abortController?.signal);
    }

    post = (url, data, options) => {
        return this.instance.post(url, data, options);
    }

    put = (url, data, options) => {
        return this.instance.put(url, data, options);
    }

    patch = (url, data, options) => {
        return this.instance.patch(url, data, options);
    }

    delete = (url, options) => {
        return this.instance.delete(url, options);
    }

    _getDateRangeHeaders(startDate, endDate, dateField) {
        return {
            "Accept": "application/json",
            "Content-Type": "application/json",
            "X-Range-Start": formatDate(startDate),
            "X-Range-End": formatDate(endDate),
            "X-Range-Start-Name": dateField + "__gte",
            "X-Range-End-Name": dateField + "__lte"
        }
    }

    runHooks(options, response) {
        let hooks = [];
        if (!isEmpty(options) && !isEmpty(options.hooks)) {
            hooks = options.hooks;
        }
        return hooks.map(hook => hook(response));
    }

    _getDateRangeOptions(options, startDate, endDate, dateField, dateFormatter) {
        const ranges = TimeDelta.getCanonicalRanges(startDate, endDate);
        return ranges.map(([start, end]) => {
            return {
                ...options,
                params: {
                    ...(options.params || {}),
                    [dateField + "__gte"]: !!dateFormatter ? dateFormatter(start) : start.toISOString(),
                    [dateField + "__lte"]: !!dateFormatter ? dateFormatter(end) : end.toISOString(),
                },
            }
        });
    }

    paginateByDate(url, options, startDate, endDate, dateField, abortController, dateFormatter) {
        const allRangeOptions = this._getDateRangeOptions(
            options,
            startDate,
            endDate,
            dateField,
            dateFormatter
        )
        const requests = allRangeOptions.map((rangeOptions, idx) => {
            if (idx === allRangeOptions.length - 1) { // Today
                rangeOptions.cache = false;
                const withCacheBuster = {
                    ...rangeOptions,
                    params: {
                        ...rangeOptions.params,
                    }
                };
                return this.get(url, withCacheBuster, abortController);
            }
            return this.get(url, {...rangeOptions, cache: true}, abortController);
        });
        return Promise.all(requests).then(responses => {
            return responses.reduce((cursor, response) => {
                this.runHooks(options, response);
                return cursor.concat(response.data.results);
            }, []);
        });
    }

    normalizeParams(params = {}) {
        const keys = Object.keys(params).sort();
        return keys.map(k => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`).join('&');
    }

    paginate = (url, options, abortController) => {
        return this.get(url, options, abortController).then(response => {
            if (isEmpty(response)) {
                return [];
            }
            if (response.data.next) {
                return this.paginate(response.data.next, {
                    params: {},
                    cache: options?.cache
                }, abortController).then(innerResults => {
                    this.runHooks(options || {}, response);
                    if (Array.isArray(response.data)) {
                        return response.data.concat(innerResults);
                    }
                    return response.data.results.concat(innerResults);
                });
            }
            this.runHooks(options || {}, response);
            if (Array.isArray(response.data)) { // Some responses are flattened for efficiency.
                return response.data;
            }
            return response.data.results;
        });
    }
}


class BackendBaseClass {

    constructor(subDomains, prefix) {
        this.subDomains = subDomains;
        this.prefix = prefix;
    }

    static wasAborted(err) {
        return err && err.code && err.code === "ERR_CANCELED";
    }

    getBackendTLD() {
        throw new Error("Not implemented");
    }

    getFrontendTLD() {
        throw new Error("Not implemented");
    }

    getFrontendBaseUrl() {
        return getBaseUrl("", this.getFrontendTLD(), "");
    }

    getBackendBaseURL() {
        return randomChoice(this.subDomains.map((subDomain) => {
            return getBaseUrl(subDomain, this.getBackendTLD(), this.prefix);
        }));
    }

    v1() {
        return randomChoice(this.subDomains.map((subDomain) => {
            return new Backend(subDomain, this.getBackendTLD(), `${this.prefix}/v1`);
        }));
    }

    v2() {
        return randomChoice(this.subDomains.map((subDomain) => {
            return new Backend(subDomain, this.getBackendTLD(), `${this.prefix}/v2`);
        }));
    }

    v3() {
        return randomChoice(this.subDomains.map((subDomain) => {
            return new Backend(subDomain, this.getBackendTLD(), `${this.prefix}/v3`);
        }));
    }

}


class APIClient extends BackendBaseClass {

    getBackendTLD() {
        return getCookie("TEST_API_TLD") || process.env.REACT_APP_API_TLD;
    }

    getFrontendTLD() {
        return getCookie("TEST_FRONTEND_TLD") || process.env.REACT_APP_FRONTEND_TLD;
    }

}


class DALClient extends BackendBaseClass {

    getBackendTLD() {
        return getCookie("TEST_API_TLD") || process.env.REACT_APP_DAL_TLD || "";
    }

}


class SSOClient extends BackendBaseClass {

    getBackendTLD() {
        const tld = getCookie("TEST_SSO_TLD") || process.env.REACT_APP_SSO_TLD;
        return tld;
    }

    getFrontendTLD() {
        return process.env.REACT_APP_SSO_FRONTEND
    }

    getLoginURL() {
        return this.getSSOFrontendBaseUrl() + "?a=7";
    }

    getLogoutURL() {
        return this.getSSOFrontendBaseUrl() + "?a=6";
    }

}


class LabClient extends BackendBaseClass {
    getBackendTLD() {
        return getCookie("TEST_LAB_TLD") || process.env.REACT_APP_LAB_TLD || "localhost:8008";
    }
}


class DutchieClient extends BackendBaseClass {

    getBackendTLD() {
        return getCookie("TEST_DUTCHIE_TLD") || process.env.REACT_APP_DUTCHIE_TLD || "localhost:4200";
    }

}

class AlpineIQClient extends BackendBaseClass {

    getBackendTLD() {
        return getCookie("TEST_ALPINEIQ_TLD") || process.env.REACT_APP_ALPINEIQ_TLD || "localhost:8080";
    }

}


class MetrcClient extends BackendBaseClass {

    getBackendTLD() {
        return getCookie("TEST_METRC_TLD") || process.env.REACT_APP_METRC_TLD || "localhost:8888";
    }

}

const apiSubdomains = getSubdomains(process.env.REACT_APP_API_SUBDOMAINS);
const dalSubdomains = getSubdomains(process.env.REACT_APP_DAL_SUBDOMAINS || process.env.REACT_APP_API_SUBDOMAINS);
const ssoSubDomains = getSubdomains(process.env.REACT_APP_SSO_API_SUBDOMAINS);
const labSubDomains = getSubdomains(process.env.REACT_APP_LAB_DAL_SUBDOMAINS)


export class Client {

    static wasAborted(err) {
        return err && err.code && err.code === "ERR_CANCELED";
    }

    constructor() {
        this.api = new APIClient(apiSubdomains, "/api");
        this.dal = new DALClient(dalSubdomains, "/dal");
        this.lab = {
            dal: new LabClient(labSubDomains, "/dal"),
        }
        this.dutchie = {
            api: new DutchieClient([null], "/api"),
            dal: new DutchieClient([null], "/dal")
        };
        this.alpineiq = {
            api: new AlpineIQClient([null], "/api"),
            dal: new AlpineIQClient([null], "/dal")
        };
        this.metrc = {
            api: new MetrcClient([null], "/api"),
            dal: new MetrcClient([null], "/dal")
        };
        this.sso = {
            api: new SSOClient(ssoSubDomains, "/api"),
            dal: new SSOClient(ssoSubDomains, "/dal")
        }
        this.stripe = {
            api: new APIClient(ssoSubDomains, "/stripe"),
        }
    }

    static aborted(abortController) {
        return abortController && abortController.signal && abortController.signal.aborted;
    }

    static getSuccessMessage = (response, fallback) => {
        if (isEmpty(response) || isEmpty(response.data)) {
            return isEmpty(fallback) ? "Success" : fallback;
        }
        if (!isEmpty(response.data.detail)) {
            return response.data.detail;
        }
        if (!isEmpty(response.data.message)) {
            return response.data.message;
        }
        return isEmpty(fallback) ? "Success" : fallback;
    }

    static getErrorMessage = (err, abortController) => {
        /*
        Returns [non-field-errors, field-errors]
         */
        if (err?.response?.status === 429) {
            return ["You are being rate limited. Please try again later.", {}];
        } else if (err?.response?.status >= 500) {
            return ["An unexpected error occurred. Please try again later.", {}];
        }
        if (Client.aborted(abortController)) {
            return [ABORTED, {}];
        }
        if (err?.response?.status === 400) {
            return [null, err.response.data];
        }
        if (err?.response?.status < 400) {
            return [null, {status: err.response.status}];
        }
        const response = err.response;
        const data = {...response.data, status: response.status};
        if (!isEmpty(data.detail)) {
            return [data.detail, {...data}];
        } else if (!isEmpty(data.message)) {
            return [data.message, {...data}];
        } else if (!isEmpty(data.non_field_errors)) {
            return [data.non_field_errors, {...data}];
        } else if (response.status === 401) {
            return ["You must be logged in to do that.", {...data}];
        } else if (response.status === 403) {
            return ["You don't have privileges to do that.", {...data}];
        }
        return ["An unexpected error occurred. Please try again later.", {...data}];
    }
}

export default new Client();
